import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Stack,
  IconButton,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  BugReport as BugIcon,
  Build as BuildIcon,
  Lightbulb as FeatureIcon,
  PriorityHigh as PriorityHighIcon
} from '@mui/icons-material';

const TicketCreateModal = ({ open, onClose, onSubmit, contactReasons }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'Bug',
    priority: 'Medium',
    dueDate: ''
  });

  const [errors, setErrors] = useState({
    title: '',
    description: '',
    type: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleSubmit = () => {
    let valid = true;
    const newErrors = { ...errors };

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
      valid = false;
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
      valid = false;
    }

    setErrors(newErrors);

    if (valid) {
      onSubmit({
        ...formData,
        status: 'Open',
        createdBy: 'currentUserId',
        comments: [],
        attachments: []
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Create New Ticket</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Title *"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={!!errors.title}
            helperText={errors.title}
          />

          <TextField
            fullWidth
            label="Description *"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={4}
            error={!!errors.description}
            helperText={errors.description}
          />

          <FormControl fullWidth>
            <InputLabel>Type *</InputLabel>
            <Select
              name="type"
              value={formData.type}
              label="Type *"
              onChange={handleChange}
            >
              {contactReasons?.map((reason) => (
                <MenuItem key={reason.value} value={reason.value}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {reason.value === 'bug' ? <BugIcon /> : 
                     reason.value === 'feature' ? <FeatureIcon /> : <BuildIcon />}
                    <span>{reason.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              name="priority"
              value={formData.priority}
              label="Priority"
              onChange={handleChange}
            >
              <MenuItem value="Low">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PriorityHighIcon />
                  <span>Low</span>
                </Stack>
              </MenuItem>
              <MenuItem value="Medium">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PriorityHighIcon color="warning" />
                  <span>Medium</span>
                </Stack>
              </MenuItem>
              <MenuItem value="High">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PriorityHighIcon color="error" />
                  <span>High</span>
                </Stack>
              </MenuItem>
              <MenuItem value="Critical">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PriorityHighIcon color="error" />
                  <span>Critical</span>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Due Date"
            name="dueDate"
            type="date"
            value={formData.dueDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          startIcon={<CloseIcon />}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          startIcon={<CheckIcon />}
          sx={{ borderRadius: 2 }}
        >
          Create Ticket
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketCreateModal;